import { useTranslation } from "react-i18next";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { EmptyState } from "../EmptyState/EmptyState";
import styles from "./Table.module.css";

interface Column<T> {
	key: string;
	header: string;
	render?: (row: T) => React.ReactNode;
	width?: string;
	sortable?: boolean;
}

interface TableProps<T> {
	columns: Column<T>[];
	data: T[];
	isLoading?: boolean;
	emptyMessage?: string;
	keyExtractor: (row: T) => string;
	sortKey?: string | undefined;
	sortDir?: "asc" | "desc" | undefined;
	onSort?: ((key: string, dir: "asc" | "desc") => void) | undefined;
	maxHeight?: string | undefined;
	className?: string | undefined;
	onRowClick?: ((row: T) => void) | undefined;
}

export function Table<T>({
	columns,
	data,
	isLoading,
	emptyMessage,
	keyExtractor,
	sortKey,
	sortDir,
	onSort,
	maxHeight,
	className,
	onRowClick,
}: TableProps<T>) {
	const { t } = useTranslation();

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (data.length === 0) {
		return <EmptyState message={emptyMessage || t("noData")} />;
	}

	return (
		<div
			className={[styles.wrapper, className].filter(Boolean).join(" ")}
			style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}
		>
			<table className={styles.table}>
				<thead>
					<tr>
						{columns.map((col) => {
							const active = sortKey === col.key;
							return (
								<th key={col.key} style={{ width: col.width, minWidth: col.width }}>
									{col.sortable && onSort ? (
										<button
											type="button"
											className={styles.sortHeader}
											onClick={() => onSort(col.key, active && sortDir === "asc" ? "desc" : "asc")}
										>
											{col.header}
											{!active && <ChevronsUpDown size={14} className={styles.sortIdle} />}
											{active && sortDir === "asc" && <ChevronUp size={14} />}
											{active && sortDir !== "asc" && <ChevronDown size={14} />}
										</button>
									) : (
										col.header
									)}
								</th>
							);
						})}
					</tr>
				</thead>
				<tbody>
					{data.map((row) => (
						<tr
							key={keyExtractor(row)}
							className={onRowClick ? styles.clickableRow : undefined}
							onClick={onRowClick ? () => onRowClick(row) : undefined}
						>
							{columns.map((col) => (
								<td key={col.key} data-label={col.header} style={{ minWidth: col.width }}>
									{col.render
										? col.render(row)
										: ((row as Record<string, unknown>)[col.key] as string)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
